import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { PrivateDiscordChannelConnectionAlert } from '@modules/integrations/components/DiscordIntegration/DiscordForm/PrivateDiscordChannelConnectionAlert';
import { AddIntegrationChannelDrawerFooter } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerFooter';
import { AddIntegrationChannelDrawerHeader } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerHeader';
import { DiscordPrivateChannelConfigConfirmationModal } from '@modules/integrationsV2/components/DiscordPrivateChannelConfigConfirmationModal';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { SelectIntegrationWorkspace } from '@modules/integrationsV2/components/SelectIntegrationWorkspace';
import {
  useFetchDiscordChannels,
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { AddIntegrationChannelDrawerContentProps } from '@modules/integrationsV2/types';
import { getDescriptionMapInputForIntegrationChannel } from '@modules/integrationsV2/utils';
import { Autocomplete, Stack, TextField } from '@mui/material';
import {
  DiscordChannelInput,
  IntegrationChannelType,
  IntegrationDescriptionInput,
} from 'generated/graphql';
import { useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';

export const AddDiscordIntegrationChannelDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationType,
  integrationData,
  channelDescriptions,
}: AddIntegrationChannelDrawerContentProps) => {
  const { openModal } = useMuiModalContext();

  const [selectedChannel, setSelectedChannel] =
    useState<DiscordChannelInput | null>(null);
  const [selectedWorkspaceID, setSelectedWorkspaceID] = useState(integrationID);
  const [descriptionInput, setDescriptionInput] = useState('');

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Discord,
      integrationType,
    });

  const { data: channels, status: discordChannelsStatus } =
    useFetchDiscordChannels(selectedWorkspaceID);

  const existingChannels = integrationData.discord?.subscribedChannels ?? [];

  const [
    validateForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Discord,
    integrationType,
    hasValidFormFields: !!selectedChannel,
    descriptionInput,
    currentDescription: '',
  });

  const integrateDiscordChannel = async (
    selectedChannel: DiscordChannelInput | null,
    addAnother = false,
  ) => {
    const isValid = await validateForm();

    if (!isValid || !selectedChannel) {
      return;
    }

    let updateDesciptionInput: IntegrationDescriptionInput | undefined;

    if (descriptionInput) {
      updateDesciptionInput = {
        descriptionMap: getDescriptionMapInputForIntegrationChannel(
          channelDescriptions,
          {
            description: descriptionInput.trim(),
            sourceID: selectedChannel.id,
          },
        ),
      };
    }

    // Remove duplicates
    const subscribedChannels = [
      ...new Map(
        [...existingChannels, selectedChannel].map((channel) => [
          channel.id,
          channel,
        ]),
      ).values(),
    ];

    const { errors } = await updateIntegration({
      integrationID: selectedWorkspaceID,
      isActive: true,
      updateData: {
        discord: {
          subscribedChannels,
        },
      },
      updateDescription: updateDesciptionInput,
    });

    if (!!errors?.length) {
      return;
    }

    if (!addAnother) {
      onDrawerClose();
      return;
    }

    setSelectedChannel(null);
    setDescriptionInput('');
  };

  const onIntegrate = (addAnother = false) => {
    if (!selectedChannel) {
      return;
    }

    openModal({
      contentComponent: () => (
        <DiscordPrivateChannelConfigConfirmationModal
          onConfirm={() => integrateDiscordChannel(selectedChannel, addAnother)}
          channelName={selectedChannel.name}
        />
      ),
      modalProps: {
        maxWidth: 'md',
        fullWidth: false,
        PaperProps: {
          sx: {
            width: '700px',
          },
        },
      },
    });
  };

  const addedChannelIDsSet = new Set(existingChannels.map((c) => c.id));

  const options = channels.filter(
    (channel) => !addedChannelIDsSet.has(channel.id),
  );

  return (
    <Stack height="100%" justifyContent="space-between">
      <Stack>
        <AddIntegrationChannelDrawerHeader title="Integrate a Discord Channel" />
        <Stack spacing={4} p={3}>
          <SelectIntegrationWorkspace
            selectedWorkspaceID={selectedWorkspaceID}
            onChange={(workspaceID) => setSelectedWorkspaceID(workspaceID)}
            channelType={IntegrationChannelType.Discord}
            integrationType={integrationType}
          />
          <Stack spacing={1}>
            <Autocomplete
              fullWidth
              multiple={false}
              value={selectedChannel}
              options={options}
              getOptionLabel={(option) => option.name}
              onChange={(_, selected) =>
                selected && setSelectedChannel(selected)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Discord Channel"
                  required
                />
              )}
              componentsProps={{ paper: { elevation: 8 } }}
              loading={IsLoading(discordChannelsStatus)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              clearIcon={null}
            />
            <PrivateDiscordChannelConnectionAlert />
          </Stack>
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            isValidationInProgress={isValidatingDescription}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isPreValidatedDescription={isPreValidatedDescription}
            integrationType={integrationType}
          />
        </Stack>
      </Stack>
      <AddIntegrationChannelDrawerFooter
        onDrawerClose={onDrawerClose}
        onIntegrateAndAddAnother={() => onIntegrate(true)}
        onIntegrate={() => onIntegrate()}
        isIntegrating={IsLoading(updateIntegrationStatus)}
        isDisabled={isDisabled}
        shouldOpenCancelConfirmationDialog={
          !!selectedChannel || !!descriptionInput
        }
      />
    </Stack>
  );
};
